import React, { useEffect, useState, useCallback } from 'react';
import AdminHeader from '../components/AdminHeader';
import {
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Switch, MenuItem, Select,
  FormControlLabel, Chip,
} from '@mui/material';
import { ConciergeServiceService } from '../services/ConciergeServiceService';

/**
 * Admin screen for managing Concierge Services and their per-service discounts.
 *
 * A discount is attached to each service:
 *   discount: { type: 'percent'|'amount', value, label, startsAt, endsAt, isActive }
 *
 * The backend applies discounts server-side on every checkout.
 * Guests see a green "X% OFF" pill on service cards.
 */
export default function ConciergeServicesAdminScreen() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ConciergeServiceService.list();
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleActive = async (svc) => {
    setSavingId(svc._id);
    try {
      await ConciergeServiceService.update(svc._id, { isActive: !svc.isActive });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle');
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveDiscount = async () => {
    if (!editing) return;
    setSavingId(editing._id);
    try {
      await ConciergeServiceService.update(editing._id, {
        basePriceCents: editing.basePriceCents,
        discount: editing.discount,
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save discount');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="flex w-full h-screen overflow-auto">
      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader
            heading={'Concierge Services'}
            pages={'Pages / Concierge Services'}
          />
        </div>

        <div className="px-10 py-6">
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          {loading ? (
            <div className="flex justify-center py-24"><CircularProgress /></div>
          ) : (
            <div className="bg-white rounded-xl p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2">Service</th>
                    <th className="py-2">Category</th>
                    <th className="py-2">Base</th>
                    <th className="py-2">Discount</th>
                    <th className="py-2">Active</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc) => {
                    const d = svc.discount;
                    const active = d?.isActive && d?.type && d?.value > 0;
                    return (
                      <tr key={svc._id} className="border-t border-gray-100">
                        <td className="py-3 font-medium">{svc.displayName}</td>
                        <td className="py-3 text-gray-500">{svc.category}</td>
                        <td className="py-3">
                          {svc.basePriceCents ? `$${(svc.basePriceCents / 100).toFixed(2)}` : '—'}
                        </td>
                        <td className="py-3">
                          {active ? (
                            <Chip
                              size="small"
                              color="success"
                              label={
                                d.type === 'percent'
                                  ? `${d.value}% OFF`
                                  : `$${d.value} OFF`
                              }
                            />
                          ) : (
                            <span className="text-gray-400">none</span>
                          )}
                        </td>
                        <td className="py-3">
                          <Switch
                            size="small"
                            checked={Boolean(svc.isActive)}
                            disabled={savingId === svc._id}
                            onChange={() => handleToggleActive(svc)}
                          />
                        </td>
                        <td className="py-3 text-right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setEditing({
                              ...svc,
                              discount: svc.discount || { type: 'percent', value: 0, label: '', isActive: false },
                            })}
                          >
                            Edit discount
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Discount for {editing?.displayName}</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4 pt-2">
              <TextField
                label="Base price (cents)"
                type="number"
                value={editing?.basePriceCents || ''}
                onChange={(e) => setEditing((prev) => ({
                  ...prev,
                  basePriceCents: e.target.value ? Number(e.target.value) : null,
                }))}
                helperText="Optional reference price in cents (e.g. 50000 = $500). Used to show 'Was / Now'."
                fullWidth
              />

              <Select
                value={editing?.discount?.type || 'percent'}
                onChange={(e) => setEditing((prev) => ({
                  ...prev,
                  discount: { ...prev.discount, type: e.target.value },
                }))}
                fullWidth
              >
                <MenuItem value="percent">Percent off</MenuItem>
                <MenuItem value="amount">Dollar amount off</MenuItem>
              </Select>

              <TextField
                label={editing?.discount?.type === 'amount' ? 'Dollars off' : 'Percent off (0-100)'}
                type="number"
                value={editing?.discount?.value ?? 0}
                onChange={(e) => setEditing((prev) => ({
                  ...prev,
                  discount: { ...prev.discount, value: Number(e.target.value) },
                }))}
                fullWidth
              />

              <TextField
                label="Badge label (optional)"
                value={editing?.discount?.label || ''}
                onChange={(e) => setEditing((prev) => ({
                  ...prev,
                  discount: { ...prev.discount, label: e.target.value },
                }))}
                helperText="Leave blank to auto-generate (e.g. '15% OFF')."
                fullWidth
              />

              <div className="flex gap-4">
                <TextField
                  label="Starts at"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={editing?.discount?.startsAt ? String(editing.discount.startsAt).slice(0, 16) : ''}
                  onChange={(e) => setEditing((prev) => ({
                    ...prev,
                    discount: { ...prev.discount, startsAt: e.target.value || null },
                  }))}
                  fullWidth
                />
                <TextField
                  label="Ends at"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={editing?.discount?.endsAt ? String(editing.discount.endsAt).slice(0, 16) : ''}
                  onChange={(e) => setEditing((prev) => ({
                    ...prev,
                    discount: { ...prev.discount, endsAt: e.target.value || null },
                  }))}
                  fullWidth
                />
              </div>

              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(editing?.discount?.isActive)}
                    onChange={(e) => setEditing((prev) => ({
                      ...prev,
                      discount: { ...prev.discount, isActive: e.target.checked },
                    }))}
                  />
                )}
                label="Discount active"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)} disabled={savingId}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveDiscount}
              disabled={savingId}
            >
              {savingId ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
